import { Globe, History, ShieldCheck, Sparkles } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { AppShell } from '@/components/px/AppShell'
import { ConceptBadge } from '@/components/px/ConceptBadge'
import { DensityNote } from '@/components/px/DensityNote'
import { EmptyPanel } from '@/components/px/EmptyPanel'
import { ErrorPanel } from '@/components/px/ErrorPanel'
import { LoadingPanel } from '@/components/px/LoadingPanel'
import { SectionLabel } from '@/components/px/SectionLabel'
import { StatusPill, type StatusTone } from '@/components/px/StatusPill'
import { libraryNav } from '@/components/px/nav'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  fetchPlaybooks,
  playbooksRetryClicked,
  selectPlaybooks,
  selectPlaybooksCache,
  selectPlaybooksError,
  selectPolicyError,
  selectTenantPolicy,
} from '@/features/playbooks/playbooksSlice'
import { useApiErrorMessage } from '@/i18n/useApiErrorMessage'
import { useAppDispatch, useAppSelector } from '@/store'
import {
  CHANGE_LOG,
  CONTRACT_TYPES,
  COUNTRY_LAYERS,
  DENSITY_NOTE,
  EXEMPLAR_TYPE,
  GATE_RULES,
  PHASE_ROWS,
  PHASE_TABLE,
  PLAYBOOK_CHROME,
  PLAYBOOK_COPY,
  PLAYBOOK_OWNER,
  PROJECTS_HREF,
  SUMMARY_CARD,
  agentHref,
  clientGateCount,
  droppedPhases,
  keptPhases,
  projectsOnType,
  summaryFor,
  type ContractSummary,
  type ContractType,
  type PhaseRow,
  type Segment,
} from '@/demo/content/playbookConfig'
import type { Phase } from '@/demo/data'
import { cn } from '@/lib/utils'

/**
 * Gate → canonical AA pill tone. A client gate is decided furniture — the
 * aubergine says a signature outside this building closes the phase — and the
 * two internal gate types stay neutral, because nothing is being claimed.
 */
const GATE_TONE: Record<Phase['gate'], StatusTone> = {
  'Client sign-off': 'decided',
  Internal: 'neutral',
  Quarterly: 'neutral',
}

const HEAD_CLASS = 'text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground'

const CHIP_CLASS =
  'inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1 text-[13px] text-ink-soft transition-colors hover:bg-muted data-[state=active]:border-mint data-[state=active]:bg-accent data-[state=active]:font-semibold data-[state=active]:text-accent-foreground'

/** Prose in which some words are real destinations (never a dead phrase). */
function Prose({ segments, linkClassName }: { segments: readonly Segment[]; linkClassName?: string }) {
  return (
    <>
      {segments.map((segment, index) =>
        segment.kind === 'text' ? (
          <span key={index}>{segment.text}</span>
        ) : (
          <Link
            key={index}
            to={segment.href}
            className={cn('font-medium underline underline-offset-2 hover:no-underline', linkClassName)}
          >
            {segment.label}
          </Link>
        )
      )}
    </>
  )
}

/** One phase of the expanded playbook: what it owes, who closes it, what runs in it. */
function PlaybookRow({ row }: { row: PhaseRow }) {
  const { phase, agents, signatory } = row

  return (
    <TableRow>
      <TableHead scope="row" className="h-auto max-w-[11rem] whitespace-normal py-3 pl-3 align-top font-normal">
        <span className="block text-sm font-medium text-foreground">{phase.name}</span>
        <span className="mt-0.5 block text-xs text-muted-foreground">{row.position}</span>
        <span className="block text-xs text-muted-foreground">{phase.subLabel}</span>
      </TableHead>

      <TableCell className="max-w-[22rem] whitespace-normal py-3 align-top text-sm leading-relaxed text-foreground">
        {row.produces}
      </TableCell>

      <TableCell className="py-3 align-top">
        <StatusPill tone={GATE_TONE[phase.gate]}>{phase.gate}</StatusPill>
      </TableCell>

      <TableCell className="max-w-[13rem] whitespace-normal py-3 align-top">
        <span className="block text-xs text-muted-foreground">{signatory.role}</span>
        <Link
          to={signatory.href}
          title={signatory.title}
          className="text-sm font-medium text-foreground underline underline-offset-2 hover:no-underline"
        >
          {signatory.name}
        </Link>
        <span className="mt-0.5 block text-xs text-muted-foreground">{signatory.meta}</span>
      </TableCell>

      <TableCell className="max-w-[16rem] whitespace-normal py-3 align-top">
        <ul>
          {agents.map((agent) => (
            <li key={agent.id} className="flex items-baseline gap-1.5 leading-7">
              <Sparkles className="h-3.5 w-3.5 shrink-0 translate-y-0.5 text-mint" aria-hidden />
              <Link
                to={agentHref(agent.id)}
                title={PHASE_TABLE.agentLinkTitle(agent.name)}
                className="text-sm text-foreground underline underline-offset-2 hover:no-underline"
              >
                {agent.name}
              </Link>
              {agent.phase === 'all' && (
                <span className="text-xs text-muted-foreground">{PHASE_TABLE.spanningNote}</span>
              )}
            </li>
          ))}
        </ul>
      </TableCell>

      <TableCell className="py-3 pr-3 align-top">
        <ul>
          {agents.map((agent) => (
            <li
              key={agent.id}
              className="v-figure text-sm font-semibold leading-7 tabular-nums text-foreground"
              title={PHASE_TABLE.autonomyTitle(agent)}
            >
              {agent.defaultAutonomy}
            </li>
          ))}
        </ul>
      </TableCell>
    </TableRow>
  )
}

/** A contract type PX runs but does not expand here — kept honest, not blank. */
function SummaryCard({ summary }: { summary: ContractSummary }) {
  const kept = keptPhases(summary)
  const dropped = droppedPhases(summary)
  const seen = projectsOnType(summary.type).length

  return (
    <section
      aria-label={summary.type}
      className="max-w-3xl rounded-lg border border-border bg-card px-5 py-4"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <h2 className="font-serif text-lg font-semibold">{summary.type}</h2>
        <Badge variant="outline" className="font-normal">
          {SUMMARY_CARD.seenCount(seen)}
        </Badge>
      </div>

      {summary.lines.map((line) => (
        <p key={line} className="mt-2 text-sm leading-relaxed text-foreground">
          {line}
        </p>
      ))}

      <dl className="mt-4 grid gap-3 border-t border-hairline pt-3 sm:grid-cols-3">
        <div>
          <dt>
            <SectionLabel>{SUMMARY_CARD.keepsLabel}</SectionLabel>
          </dt>
          <dd className="mt-1 text-sm text-foreground">
            <span className="v-figure font-semibold tabular-nums">
              {SUMMARY_CARD.phaseCount(kept.length)}
            </span>
            <span className="mt-0.5 block text-xs text-ink-soft">
              {kept.map((phase) => phase.name).join(' · ')}
            </span>
          </dd>
        </div>
        <div>
          <dt>
            <SectionLabel>{SUMMARY_CARD.dropsLabel}</SectionLabel>
          </dt>
          <dd className="mt-1 text-sm text-ink-soft">
            {dropped.length === 0
              ? SUMMARY_CARD.dropsNone
              : dropped.map((phase) => phase.name).join(' · ')}
          </dd>
        </div>
        <div>
          <dt>
            <SectionLabel>{SUMMARY_CARD.clientGatesLabel}</SectionLabel>
          </dt>
          <dd className="mt-1 text-sm text-foreground">
            <span className="v-figure font-semibold tabular-nums">{clientGateCount(summary)}</span>
            <span className="mt-0.5 block text-xs text-ink-soft">
              {kept
                .filter((phase) => phase.gate === 'Client sign-off')
                .map((phase) => phase.name)
                .join(' · ')}
            </span>
          </dd>
        </div>
      </dl>

      <p className="mt-3 border-t border-hairline pt-3 text-xs text-muted-foreground">
        {SUMMARY_CARD.expandHint}
        {EXEMPLAR_TYPE}
      </p>
    </section>
  )
}

/**
 * The LIVE playbooks panel (PX-W6.5, AC-13): the org's read-only playbook
 * rows and the tenant policy, straight from their GET routes. Rendering only
 * — no write verb exists; the demo edit affordances below keep their badge.
 */
function LivePlaybooksPanels() {
  const { t } = useTranslation(['playbooks', 'common'])
  const toMessage = useApiErrorMessage()
  const dispatch = useAppDispatch()

  const playbooks = useAppSelector(selectPlaybooks)
  const policy = useAppSelector(selectTenantPolicy)
  const cache = useAppSelector(selectPlaybooksCache)
  const error = useAppSelector(selectPlaybooksError)
  const policyError = useAppSelector(selectPolicyError)

  useEffect(() => {
    void dispatch(fetchPlaybooks())
  }, [dispatch])

  return (
    <div className="mt-5 flex flex-col gap-4">
      <section aria-label={t('live.playbooksHeading')} className="rounded-lg border border-border bg-card">
        <div className="border-b border-border px-4 py-3">
          <SectionLabel>{t('live.playbooksHeading')}</SectionLabel>
        </div>
        <div className="px-4 py-3">
          {cache.loading ? (
            <LoadingPanel rows={2} />
          ) : error !== null ? (
            <ErrorPanel
              error={error}
              onRetryClicked={() => {
                dispatch(playbooksRetryClicked())
                void dispatch(fetchPlaybooks())
              }}
            />
          ) : playbooks.length === 0 ? (
            <EmptyPanel body={t('live.playbooksEmpty')} />
          ) : (
            <Table>
              <TableCaption className="sr-only">{t('live.playbooksCaption')}</TableCaption>
              <TableHeader>
                <TableRow>
                  <TableHead scope="col" className={HEAD_CLASS + ' pl-3'}>
                    {t('live.columns.name')}
                  </TableHead>
                  <TableHead scope="col" className={HEAD_CLASS}>
                    {t('live.columns.status')}
                  </TableHead>
                  <TableHead scope="col" className={HEAD_CLASS}>
                    {t('live.columns.version')}
                  </TableHead>
                  <TableHead scope="col" className={HEAD_CLASS + ' pr-3'}>
                    {t('live.columns.phases')}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {playbooks.map((row) => (
                  <TableRow key={row.id ?? row.name}>
                    <TableHead scope="row" className="h-auto py-2.5 pl-3 align-top font-normal">
                      <span className="text-sm font-medium text-foreground">{row.name}</span>
                      {row.id !== null && (
                        <span className="mt-0.5 block font-mono text-xs text-muted-foreground">
                          {row.id}
                        </span>
                      )}
                    </TableHead>
                    <TableCell className="py-2.5 align-top">
                      <StatusPill tone="neutral">{row.status}</StatusPill>
                    </TableCell>
                    <TableCell className="py-2.5 align-top font-mono text-xs text-ink-soft">
                      {row.version}
                    </TableCell>
                    <TableCell className="py-2.5 pr-3 align-top text-sm text-ink-soft">
                      {row.phases.length}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </section>

      <section aria-label={t('live.policyHeading')} className="rounded-lg border border-border bg-card">
        <div className="border-b border-border px-4 py-3">
          <SectionLabel>{t('live.policyHeading')}</SectionLabel>
        </div>
        <div className="px-4 py-3">
          {cache.loading && policy === null && policyError === null ? (
            <LoadingPanel rows={2} />
          ) : policyError !== null ? (
            /* Review #3: a refused/broken policy read SURFACES here — never a
             * silently empty panel. The table above keeps its own rows. */
            <div role="alert">
              <p className="text-sm text-blocked">{toMessage(policyError)}</p>
              <button
                type="button"
                onClick={() => {
                  dispatch(playbooksRetryClicked())
                  void dispatch(fetchPlaybooks())
                }}
                className="mt-3 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90"
              >
                {t('common:states.retry')}
              </button>
            </div>
          ) : policy === null ? (
            <EmptyPanel body={t('live.policyEmpty')} />
          ) : (
            <dl className="grid gap-x-8 gap-y-3 sm:grid-cols-2 lg:grid-cols-3">
              <div>
                <dt>
                  <SectionLabel>{t('live.policy.region')}</SectionLabel>
                </dt>
                <dd className="mt-1 text-sm text-foreground">{policy.region}</dd>
              </div>
              <div>
                <dt>
                  <SectionLabel>{t('live.policy.dataClassification')}</SectionLabel>
                </dt>
                <dd className="mt-1 text-sm text-foreground">{policy.dataClassification}</dd>
              </div>
              <div>
                <dt>
                  <SectionLabel>{t('live.policy.retentionDays')}</SectionLabel>
                </dt>
                <dd className="mt-1 text-sm text-foreground">
                  {policy.retentionDays === null || policy.retentionDays === undefined
                    ? t('common:values.none')
                    : t('live.policy.days', { count: policy.retentionDays })}
                </dd>
              </div>
              <div>
                <dt>
                  <SectionLabel>{t('live.policy.spendCapMinutes')}</SectionLabel>
                </dt>
                <dd className="mt-1 text-sm text-foreground">
                  {t('live.policy.minutes', { count: policy.spendCapMinutes })}
                </dd>
              </div>
              <div>
                <dt>
                  <SectionLabel>{t('live.policy.clientSelfOnboarding')}</SectionLabel>
                </dt>
                <dd className="mt-1 text-sm text-foreground">
                  {policy.clientSelfOnboarding ? t('common:values.yes') : t('common:values.no')}
                </dd>
              </div>
              <div>
                <dt>
                  <SectionLabel>{t('live.policy.gateAgeingThresholdDays')}</SectionLabel>
                </dt>
                <dd className="mt-1 text-sm text-foreground">
                  {t('live.policy.days', { count: policy.gateAgeingThresholdDays })}
                </dd>
              </div>
              <div className="sm:col-span-2 lg:col-span-3">
                <dt>
                  <SectionLabel>{t('live.policy.defaultModelRouting')}</SectionLabel>
                </dt>
                <dd className="mt-1">
                  <ul>
                    {policy.defaultModelRouting.map((route) => (
                      <li
                        key={route.category}
                        className="flex items-baseline gap-2 border-t border-hairline py-1.5 text-sm first:border-t-0 first:pt-0"
                      >
                        <span className="text-ink-soft">{route.category}</span>
                        <span className="font-mono text-xs text-foreground">
                          {route.provider} · {route.model}
                        </span>
                      </li>
                    ))}
                  </ul>
                </dd>
              </div>
              <div className="sm:col-span-2">
                <dt>
                  <SectionLabel>{t('live.policy.guardrailPolicy')}</SectionLabel>
                </dt>
                <dd className="mt-1 flex flex-wrap gap-1.5">
                  {policy.guardrailPolicy.map((entry) => (
                    <Badge key={entry.key} variant="outline" className="font-normal">
                      {entry.key}: {entry.value}
                    </Badge>
                  ))}
                </dd>
              </div>
              <div>
                <dt>
                  <SectionLabel>{t('live.policy.agentRegistrySubset')}</SectionLabel>
                </dt>
                <dd className="mt-1 text-sm text-ink-soft">
                  {policy.agentRegistrySubset.join(', ')}
                </dd>
              </div>
            </dl>
          )}
        </div>
      </section>
    </div>
  )
}

/**
 * 2.10 Playbook configuration — the method itself, configurable per contract
 * type. PX-W6.5 adds the LIVE playbooks + tenant-policy reads above the demo
 * sections (which keep their content and their concept badge — W6.6 retires
 * demo). One type is expanded to its seven phases, their gates, their
 * signatures and the agents switched on; the other four state, honestly, what
 * they keep and what they never open.
 */
export default function PlaybookConfigPage() {
  const { t } = useTranslation(['playbooks', 'common'])
  const [selected, setSelected] = useState<ContractType>(EXEMPLAR_TYPE)

  const onTypeChanged = useCallback((value: string) => {
    const next = CONTRACT_TYPES.find((type) => type === value)
    if (next) setSelected(next)
  }, [])

  return (
    <AppShell
      moduleLabel={PLAYBOOK_CHROME.moduleLabel}
      // Neither names the fixture's exemplar contract type any more (fix-back #2).
      breadcrumb={t('playbooks:chrome.breadcrumb')}
      contextPill={t('playbooks:chrome.contextPill')}
      nav={libraryNav}
    >
      <header className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1">
        <h1 className="font-serif text-2xl font-semibold">{PLAYBOOK_COPY.title}</h1>
        <p className="text-sm text-muted-foreground">{PLAYBOOK_COPY.lede}</p>
      </header>
      <p className="mt-2 max-w-4xl text-sm leading-relaxed text-ink-soft">{PLAYBOOK_COPY.replaces}</p>

      <LivePlaybooksPanels />

      <Tabs value={selected} onValueChange={onTypeChanged} className="mt-5">
        <div className="flex items-center justify-between gap-2">
          <SectionLabel>{PLAYBOOK_COPY.chipsLegend}</SectionLabel>
          <ConceptBadge />
        </div>
        <TabsList variant="line" className="h-auto flex-wrap justify-start gap-1.5 p-0">
          {CONTRACT_TYPES.map((type) => (
            <TabsTrigger key={type} value={type} className={CHIP_CLASS}>
              {type}
            </TabsTrigger>
          ))}
        </TabsList>

        <p className="text-sm text-ink-soft">
          {PLAYBOOK_COPY.appliedPrefix}
          <Link
            to={PROJECTS_HREF}
            className="font-medium text-foreground underline underline-offset-2 hover:no-underline"
          >
            {PLAYBOOK_COPY.appliedLink}
          </Link>
          {PLAYBOOK_COPY.appliedSuffix}{' '}
          <span className="text-muted-foreground">
            {PLAYBOOK_COPY.ownerPrefix}
            <Link to={PLAYBOOK_OWNER.href} className="underline underline-offset-2 hover:no-underline">
              {PLAYBOOK_OWNER.name}
            </Link>
            {` · ${PLAYBOOK_OWNER.meta}`}
          </span>
        </p>

        <TabsContent value={EXEMPLAR_TYPE} className="mt-1">
          <section
            aria-label={PHASE_TABLE.label}
            className="rounded-lg border border-border bg-card"
          >
            <Table>
              <TableCaption className="sr-only">{PHASE_TABLE.caption}</TableCaption>
              <TableHeader>
                <TableRow>
                  <TableHead scope="col" className={cn(HEAD_CLASS, 'pl-3')}>
                    {PHASE_TABLE.columns.phase}
                  </TableHead>
                  <TableHead scope="col" className={HEAD_CLASS}>
                    {PHASE_TABLE.columns.produces}
                  </TableHead>
                  <TableHead scope="col" className={HEAD_CLASS}>
                    {PHASE_TABLE.columns.gate}
                  </TableHead>
                  <TableHead scope="col" className={HEAD_CLASS}>
                    {PHASE_TABLE.columns.signedBy}
                  </TableHead>
                  <TableHead scope="col" className={HEAD_CLASS}>
                    {PHASE_TABLE.columns.agents}
                  </TableHead>
                  <TableHead scope="col" className={cn(HEAD_CLASS, 'pr-3')}>
                    {PHASE_TABLE.columns.autonomy}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {PHASE_ROWS.map((row) => (
                  <PlaybookRow key={row.phase.key} row={row} />
                ))}
              </TableBody>
            </Table>
          </section>
        </TabsContent>

        {CONTRACT_TYPES.filter((type) => type !== EXEMPLAR_TYPE).map((type) => {
          const summary = summaryFor(type)
          return (
            <TabsContent key={type} value={type} className="mt-1">
              {summary && <SummaryCard summary={summary} />}
            </TabsContent>
          )
        })}
      </Tabs>

      <div className="mt-5 grid gap-4 lg:grid-cols-3">
        {/* The order of the method, and the one legal way to break it. */}
        <section
          aria-labelledby="gate-rules-label"
          className="rounded-lg border border-border bg-card px-4 py-4"
        >
          <div id="gate-rules-label" className="flex items-center gap-1.5">
            <ShieldCheck className="h-3.5 w-3.5 text-ink-soft" aria-hidden />
            <SectionLabel>{GATE_RULES.label}</SectionLabel>
          </div>
          <p className="mt-1.5 text-xs text-muted-foreground">{GATE_RULES.lede}</p>
          <ul className="mt-3">
            {GATE_RULES.rules.map((rule) => (
              <li key={rule.title} className="border-t border-hairline py-2.5 first:border-t-0 first:pt-0">
                <span className="block text-sm font-semibold text-foreground">{rule.title}</span>
                <p className="mt-0.5 text-sm leading-relaxed text-ink-soft">
                  <Prose segments={rule.prose} />
                </p>
              </li>
            ))}
          </ul>
        </section>

        {/* What an office may add — and the thing no office may do. */}
        <section
          aria-labelledby="country-layers-label"
          className="rounded-lg border border-border bg-card px-4 py-4"
        >
          <div id="country-layers-label" className="flex items-center gap-1.5">
            <Globe className="h-3.5 w-3.5 text-ink-soft" aria-hidden />
            <SectionLabel>{COUNTRY_LAYERS.label}</SectionLabel>
          </div>
          <p className="mt-1.5 text-xs text-muted-foreground">{COUNTRY_LAYERS.lede}</p>
          <ul className="mt-3">
            {COUNTRY_LAYERS.layers.map((countryLayer) => (
              <li
                key={countryLayer.code}
                className="border-t border-hairline py-2.5 first:border-t-0 first:pt-0"
              >
                <span className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                  <StatusPill tone="people">{countryLayer.code}</StatusPill>
                  <span className="text-sm font-semibold text-foreground">{countryLayer.adds}</span>
                </span>
                <span className="mt-1 block text-xs text-muted-foreground">
                  {`${COUNTRY_LAYERS.addsLabel} ${countryLayer.phase.name} · ${countryLayer.position} · ${countryLayer.officeName}`}
                </span>
                <p className="mt-1 text-sm leading-relaxed text-ink-soft">{countryLayer.detail}</p>
                <span className="mt-1 block text-xs text-muted-foreground">
                  {`${COUNTRY_LAYERS.ownerLabel} `}
                  <Link
                    to={countryLayer.ownerHref}
                    title={countryLayer.ownerTitle}
                    className="underline underline-offset-2 hover:no-underline"
                  >
                    {countryLayer.ownerName}
                  </Link>
                </span>
              </li>
            ))}
          </ul>
          <p className="mt-3 border-t border-hairline pt-3 text-sm leading-relaxed text-foreground">
            {COUNTRY_LAYERS.rule}
          </p>
        </section>

        {/* Versions, because a method that changes silently is not a method. */}
        <section
          aria-labelledby="change-log-label"
          className="rounded-lg border border-border bg-card px-4 py-4"
        >
          <div id="change-log-label" className="flex items-center gap-1.5">
            <History className="h-3.5 w-3.5 text-ink-soft" aria-hidden />
            <SectionLabel>{CHANGE_LOG.label}</SectionLabel>
          </div>
          <p className="mt-1.5 text-xs text-muted-foreground">{CHANGE_LOG.lede}</p>
          <ol className="mt-3">
            {CHANGE_LOG.entries.map((entry) => (
              <li
                key={entry.version}
                className="flex gap-3 border-t border-hairline py-2.5 first:border-t-0 first:pt-0"
              >
                <span className="v-figure w-7 shrink-0 text-sm font-semibold tabular-nums text-muted-foreground">
                  {entry.version}
                </span>
                <span className="min-w-0">
                  <span className="block text-sm text-foreground">
                    <Prose segments={entry.prose} />
                  </span>
                  <span className="mt-0.5 block text-xs leading-relaxed text-muted-foreground">
                    {entry.effect}
                  </span>
                </span>
              </li>
            ))}
          </ol>
        </section>
      </div>

      <DensityNote>
        <Prose segments={DENSITY_NOTE} />
      </DensityNote>
    </AppShell>
  )
}
